/**
 * Calculate the next Friday at 9am in the given timezone
 * Rentals are sent every Friday morning
 */
export function getNextFridayInTimezone(timezone: string): Date {
  const now = new Date();

  // Create a formatter to get the current day of week in the target timezone
  const dayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false,
  });

  const currentDay = dayFormatter.format(now);
  const currentHour = parseInt(timeFormatter.format(now), 10);

  // Calculate days until next Friday
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const currentDayIndex = days.indexOf(currentDay);
  const fridayIndex = 5;

  let daysUntilFriday = fridayIndex - currentDayIndex;

  // If it's Friday, check if it's before 9am in the timezone
  if (daysUntilFriday === 0) {
    // If it's past 9am on Friday, next rental is next week
    if (currentHour >= 9) {
      daysUntilFriday = 7;
    }
  } else if (daysUntilFriday < 0) {
    // If we've passed Friday this week, go to next Friday
    daysUntilFriday += 7;
  }

  // Calculate the next Friday date
  const nextFriday = new Date(now);
  nextFriday.setDate(nextFriday.getDate() + daysUntilFriday);

  // Set to 9am in the target timezone by creating a date string
  // and parsing it as if it were in that timezone
  const year = nextFriday.getFullYear();
  const month = String(nextFriday.getMonth() + 1).padStart(2, '0');
  const day = String(nextFriday.getDate()).padStart(2, '0');

  // Create a formatter to get the exact date components in the target timezone
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const dateParts = dateFormatter.formatToParts(nextFriday);
  const tzYear = dateParts.find((p) => p.type === 'year')?.value || year;
  const tzMonth = dateParts.find((p) => p.type === 'month')?.value || month;
  const tzDay = dateParts.find((p) => p.type === 'day')?.value || day;

  // Return a Date object representing 9am on that Friday in the given timezone
  // We store the UTC equivalent
  const fridayStr = `${tzYear}-${tzMonth}-${tzDay}T09:00:00`;

  // Use Intl to convert back to a proper Date
  // This gives us a Date that represents 9am in the target timezone
  const tempDate = new Date(fridayStr);

  // Calculate the offset between local and target timezone
  const localOffset = tempDate.getTimezoneOffset();
  const targetFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  });

  const targetParts = targetFormatter.formatToParts(tempDate);
  const offsetPart = targetParts.find((p) => p.type === 'timeZoneName')?.value;

  let targetOffsetMinutes = 0;
  if (offsetPart) {
    // Parse offset like "GMT+1" or "GMT-5" or "GMT+5:30"
    const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (match) {
      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      const minutes = match[3] ? parseInt(match[3], 10) : 0;
      targetOffsetMinutes = sign * (hours * 60 + minutes);
    }
  }

  // Adjust the date to represent 9am in the target timezone
  const adjustedDate = new Date(
    tempDate.getTime() - (targetOffsetMinutes + localOffset) * 60 * 1000
  );

  return adjustedDate;
}

/**
 * Format the next rental arrival time in a human-readable way
 */
export function formatNextRentalTime(timezone: string): string {
  const nextFriday = getNextFridayInTimezone(timezone);
  const now = new Date();

  const diffMs = nextFriday.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  // Format the date for display
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const formattedDate = dateFormatter.format(nextFriday);

  if (diffDays === 0) {
    if (diffHours <= 1) {
      return `in about an hour (${formattedDate})`;
    }
    return `today at 9:00 AM (in ${diffHours} hours)`;
  } else if (diffDays === 1) {
    return `tomorrow at 9:00 AM`;
  } else {
    return formattedDate;
  }
}

/**
 * Get the display name for a timezone
 */
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long',
    });
    const parts = formatter.formatToParts(new Date());
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tzName || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Get the current UTC offset for a timezone (e.g., "UTC+01:00")
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offsetPart || '';
  } catch {
    return '';
  }
}
